import { Location } from "@angular/common";
import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpParams,
    HttpRequest,
    HttpResponse,
    HttpStatusCode
} from "@angular/common/http";
import { Injectable, NgZone, inject } from "@angular/core";
import { Router } from "@angular/router";

import { Observable, Subject } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { IContent } from "~app/common/models/validationModel";
import { environment } from "~environments/environment";
import { AppInsightsService } from "./services/app-insights.service";
import { ErrorMediatorService } from "./services/error-mediator.service";
import { RaskVersionChecker } from "./services/rask-version-checker.service";

export const SkipCustomInterceptor = "X-Skip-Angular-Interceptor";

@Injectable()
export class CustomInterceptor implements HttpInterceptor {
    readonly overrideMaintenanceModeHeaderConstant = "rask-override-maintenance-mode";
    readonly raskClientVersionHttpHeaderConstant = "rask-client-version";
    readonly releaseInfoConstant = "releaseInfo";
    readonly statusCodeRaskVersionDiff = 410;

    isInOverrideMode = false;

    private readonly appInsightsService = inject(AppInsightsService);
    private readonly errorMediatorService = inject(ErrorMediatorService);
    private readonly location = inject(Location);
    private readonly ngZone = inject(NgZone);
    private readonly raskVersionChecker = inject(RaskVersionChecker);
    private readonly router = inject(Router);

    constructor() {
        const url = this.location.path();
        if (url.includes("?")) {
            const httpParams = new HttpParams({ fromString: url.split("?")[1] });
            this.isInOverrideMode = httpParams.has("overridemmode");
        }
    }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        let newHeaders = request.headers
            .set("ClientCulture", "sv-SE")
            .set(this.raskClientVersionHttpHeaderConstant, environment[this.releaseInfoConstant].buildNumber);

        if (request.method === "GET") {
            newHeaders = newHeaders
                .set("Cache-Control", "no-cache")
                .set("Pragma", "no-cache");
        }

        if (this.isInOverrideMode) {
            newHeaders = newHeaders.set(this.overrideMaintenanceModeHeaderConstant, "true");
        }

        let skipStatus;
        if (newHeaders.has(SkipCustomInterceptor)) {
            skipStatus = parseInt(newHeaders.get(SkipCustomInterceptor), 10);
            newHeaders = newHeaders.delete(SkipCustomInterceptor);
        }

        request = request.clone({
            headers: newHeaders
        });

        return next.handle(request).pipe(
            map(event => {
                this.handleResponseMessages(request, event);
                this.raskVersionChecker.requestExecuted(request.url);
                return event;
            }),
            catchError(error => this.handleResponseError(error, request, next, skipStatus))
        );
    }

    private handleErrorResponse(request: HttpRequest<any>, error: HttpErrorResponse) {
        if (request.responseType === "text" && typeof (error.error) === "string") {
            return this.handleTextResponseType(error);
        } else if (typeof (error.error) === "string") {
            return {
                message: error.error,
                messageType: "Unhandled"
            } as IContent;
        } else {
            return error.error as IContent;
        }
    }

    private handleResponseError(error: HttpErrorResponse, request: HttpRequest<any>, next: HttpHandler, skipStatus: number): Observable<any> {
        const raskApiPatterns = [
            environment.api.toLowerCase(),
            environment.apiV2.toLowerCase(),
            environment.apiProxy.toLowerCase()
        ];

        const obs = new Subject<any>();

        const requestUrl = request.url.toLowerCase();

        // Logga till AI och hoppa över vanlig hantering om anropet inte går mot rasks api
        if (!raskApiPatterns.some(pattern => requestUrl.startsWith(pattern))) {
            obs.error(error);
            return obs;
        }

        if (this.handleStatusCodeSkip(error, skipStatus)) {
            obs.error(error);
            return obs;
        }

        switch (error.status) {
            case 0:
                if (request.headers.get("skip-cors-error-dialog")) {
                    this.router.navigate(["login"]);
                    return obs;
                }
                break;
            case HttpStatusCode.Unauthorized:
                this.router.navigate(["login"], {
                    queryParams: { status: HttpStatusCode.Unauthorized },
                    skipLocationChange: true
                });
                return obs;
            case HttpStatusCode.Forbidden:
                this.router.navigate(["/permissiondenied"], { state: { skipDirtyCheck: true } });
                obs.complete();
                return obs;
            case HttpStatusCode.Gone:
                obs.error(error);
                this.raskVersionChecker.showModalVersionError();
                return obs;
            case HttpStatusCode.BadGateway:
                this.router.navigate(["/badgateway"], { state: { skipDirtyCheck: true } });
                obs.complete();
                return obs;
            case HttpStatusCode.ServiceUnavailable: {
                const hasOverrideHeader = error.headers.has(this.overrideMaintenanceModeHeaderConstant);
                this.raskVersionChecker.showModalMaintenanceError(hasOverrideHeader);
                this.router.navigate(["/maintenance"], { state: { skipDirtyCheck: true } });
                obs.complete();
                return obs;
            }
        }

        const messages: IContent = this.parseError(request, error);

        return new Observable(observer => {
            this.ngZone.run(() => {
                this.openModal(request, messages)
                    .subscribe(r => {
                        if (r === "force") {
                            const forceRequest = request.clone({ headers: request.headers.set("Force", "true") });
                            next.handle(forceRequest).subscribe({

                                next: event => observer.next(event),

                                error: err => observer.error(err),

                                complete: () => observer.complete()
                            });
                        } else {
                            observer.error(error);
                        }
                    });
            });
        });
    }

    private handleResponseMessages(request: any, event: HttpEvent<any>): void {
        if (event instanceof HttpResponse && event?.body?.messages?.length > 0) {
            this.openModal(request, {
                validationMessages: event?.body?.messages,
                messageType: "Message"
            } as IContent);
        }
    }

    private handleStatusCodeSkip(error: HttpErrorResponse, skipStatus: number): boolean {
        if (skipStatus === undefined) {
            return false;
        }

        if (skipStatus === -1 || error.status === skipStatus) {
            return true;
        }
        return undefined;
    }

    private handleTextResponseType(error: HttpErrorResponse) {
        try {
            const parsed = JSON.parse(error.error);
            if (typeof (parsed.message) === "string") {
                return {
                    message: parsed.message,
                    messageType: "Unhandled"
                } as IContent;
            }
            // eslint-disable-next-line sonarjs/no-ignored-exceptions
        } catch (e) {
        }
        return {
            message: error.error,
            messageType: "Unhandled"
        } as IContent;
    }

    private openModal(request: HttpRequest<any>, errorContent: IContent): Observable<any> {
        errorContent.message = `${errorContent.message}`;
        return this.errorMediatorService.add(errorContent, request);
    }

    private parseError(request: HttpRequest<any>, error: HttpErrorResponse): IContent {
        if (!error) {
            return null;
        } else if (error.status === 400 && error.name === "HttpErrorResponse") {
            return this.handleErrorResponse(request, error);
        } else if (error.status === 0) {
            return {
                message: `Det går inte att komma åt Rask. Det kan bero på att internetuppkoppling saknas.`,
                messageType: "Unhandled"
            };
        } else {
            this.appInsightsService.trackEvent({ name: "onRejection" }, {
                messageType: error.name,
                message: error.message,
                url: request.url
            });
            return {
                message: `${error.message} (${error.status})`,
                messageType: "Unhandled"
            };
        }
    }
}
